import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { getAuditLogs } from "../api/auditLogs";
import { getApiError } from "../api/errors";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { formatDate, titleCase } from "../utils/format";

const PAGE_SIZE = 25;
const RESOURCE_OPTIONS = ["", "product", "inventory", "document", "connector"];

export default function AuditLogs() {
  const { selectedWorkspace } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [resourceType, setResourceType] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    if (!selectedWorkspace?.public_id) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setError("");
    try {
      const response = await getAuditLogs({
        action: action.trim(),
        resourceType,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setItems(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not load audit logs."));
    } finally {
      setLoading(false);
    }
  }, [action, page, resourceType, selectedWorkspace?.public_id]);

  useEffect(() => {
    const timeout = window.setTimeout(load, action ? 350 : 0);
    return () => window.clearTimeout(timeout);
  }, [load, action]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <MainLayout>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={900} letterSpacing={-0.7}>Audit logs</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            Tenant-scoped history of sensitive business and connector actions.
          </Typography>
        </Box>
        <Chip label={`${total} events`} sx={{ alignSelf: { xs: "flex-start", md: "center" }, fontWeight: 800 }} />
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} action={<Button color="inherit" onClick={load}>Retry</Button>}>{error}</Alert>}

      <Card sx={{ mb: 3, borderRadius: 4, border: "1px solid #e2e8f0" }}>
        <CardContent sx={{ p: 2.5 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Filter by action"
                placeholder="Example: connector.revoked"
                value={action}
                onChange={(event) => { setAction(event.target.value); setPage(1); }}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Resource type"
                value={resourceType}
                onChange={(event) => { setResourceType(event.target.value); setPage(1); }}
              >
                {RESOURCE_OPTIONS.map((option) => (
                  <MenuItem key={option || "all"} value={option}>
                    {option ? titleCase(option) : "All resources"}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="outlined" onClick={() => { setAction(""); setResourceType(""); setPage(1); }} sx={{ py: 1.7, textTransform: "none", fontWeight: 800 }}>
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? <LoadingPanel label="Loading audit history…" /> : items.length === 0 ? (
        <EmptyPanel icon="🧾" title="No matching audit events" description="Try clearing the filters or perform an audited business action." />
      ) : (
        <Stack spacing={1.5}>
          {items.map((log) => {
            const expanded = expandedId === log.public_id;
            return (
              <Card key={log.public_id} sx={{ borderRadius: 3.5, border: "1px solid #e2e8f0", boxShadow: "none" }}>
                <CardContent sx={{ p: 2.25 }}>
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        <Typography fontWeight={900}>{titleCase(log.action)}</Typography>
                        <Chip label={titleCase(log.resource_type)} size="small" variant="outlined" />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        {formatDate(log.created_at)} · IP {log.ip_address || "Unknown"}
                      </Typography>
                      {log.resource_public_id && (
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace" sx={{ overflowWrap: "anywhere" }}>
                          Resource: {log.resource_public_id}
                        </Typography>
                      )}
                    </Box>
                    <Button onClick={() => setExpandedId(expanded ? "" : log.public_id)} sx={{ alignSelf: { xs: "flex-start", md: "center" }, textTransform: "none", fontWeight: 800 }}>
                      {expanded ? "Hide details" : "View details"}
                    </Button>
                  </Stack>

                  <Collapse in={expanded}>
                    <Box sx={{ mt: 2, p: 2, borderRadius: 3, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}><Typography variant="caption" color="text.secondary">Request ID</Typography><Typography variant="body2" fontFamily="monospace" sx={{ overflowWrap: "anywhere" }}>{log.request_id || "—"}</Typography></Grid>
                        <Grid item xs={12} md={6}><Typography variant="caption" color="text.secondary">User agent</Typography><Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{log.user_agent || "—"}</Typography></Grid>
                        <Grid item xs={12}><Typography variant="caption" color="text.secondary">Metadata</Typography><Box component="pre" sx={{ m: 0, mt: 0.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: 13 }}>{JSON.stringify(log.metadata || {}, null, 2)}</Box></Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}

          {pages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Pagination count={pages} page={page} onChange={(_, value) => setPage(value)} color="primary" />
            </Box>
          )}
        </Stack>
      )}
    </MainLayout>
  );
}
